/**
 * demo-clock — re-anchors hard-coded demo dates to "now" so a parked demo never
 * shows a stale absolute date. Preserves each date's offset from the authoring
 * era, so relative spacing (received 3 days ago, due in 5 days) is kept.
 *
 * Cross-surface: consumed by payment-history and service-requests fixtures so
 * both web and mobile render current dates. Pure; injectable `asOf` for tests.
 *
 * NB: the billing ledger uses month-granular anchoring in @petci/fixtures
 * (billing-clock.ts). This day-granular re-anchor is the shared-ui counterpart
 * for payment/SR dates, which the fixtures→shared-ui boundary rule keeps separate.
 */

// The wall-clock era the dated fixtures were authored against (mid-cycle of the
// authored "current" bill month, 2026-05). Keep aligned with the billing anchor.
const DEMO_REF_NOW = new Date(2026, 4, 15); // 2026-05-15

function toIsoDate(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Re-anchor a hard-coded `YYYY-MM-DD` demo date to `asOf` (default now),
 * shifting it by the days the clock has advanced past the authoring era.
 * Returns `YYYY-MM-DD`; passes through unrecognised input unchanged.
 */
export function reanchorDemoDate(originalIso: string, asOf: Date = new Date()): string {
  const orig = new Date(`${originalIso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(orig.getTime())) return originalIso;
  const deltaDays = Math.round((orig.getTime() - DEMO_REF_NOW.getTime()) / 86_400_000);
  const shifted = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate() + deltaDays);
  return toIsoDate(shifted);
}
